import { BaseService } from './base.service';
import { ErrorWrapper, ResponseWrapper } from './util';

export class DoctorService extends BaseService {
  static async getDoctors ({
    page = '',
    search = '',
    pageSize,
    type = 'all'
  } = {}) {
    try {
      const response = await this.request({ auth: true, v2: true }).post(
        '/admin/doctorDashboard',
        {
          page,
          search,
          type,
          ...(pageSize && { pageSize })
        }
      );

      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async getAllDoctors (hideDisabled = false) {
    try {
      const queryParam = hideDisabled ? '?disabled=true' : '';
      const response = await this.request({ auth: true }).get(`/admin/doctors${queryParam}`);

      return new ResponseWrapper(response, response.data.body);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async getAllDoctorsV2 () {
    try {
      const response = await this.request({ auth: true, v2: true }).get('/admin/doctor');

      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async ignoreDoctorConversation (conversationId) {
    try {
      const response = await this.request({ auth: true, v2: true }).post(`/admin/conversation/${conversationId}/ignore`);

      return new ResponseWrapper(response, response.data.body);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async getDoctorsByState (state) {
    try {
      const response = await this.request({ auth: true }).get(
        `/admin/doctor/state/${state}`
      );

      return new ResponseWrapper(response, response.data.body);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  // Payload should be an array of states
  static async getDoctorsByStateV2 (states, enddatestamp, startdatestamp, requestfrom, reassignmenttype) {
    try {
      const response = await this.request({ auth: true, v2: true }).post(
        '/admin/doctorReassignment/state-list',
        { states, enddatestamp, startdatestamp, requestfrom, reassignmenttype }
      );

      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async setDoctorSettings (id, data) {
    try {
      const response = await this.request({ auth: true, v2: true }).post(
        `/admin/doctor/${id}/settings`,
        data
      );

      return new ResponseWrapper(response, response.data.body);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async getOooRequests ({
    tab = 'all',
    search,
    filters,
    sortBy = 'startdatestamp',
    sortDir = 'desc',
    limit = 25,
    offset = 0,
  } = {}) {
    try {
      const payload = { tab, limit, offset, sortBy, sortDir };
      if (search) {
        payload.search = search;
      }

      if (filters && filters.length) {
        payload.filters = filters;
      }

      const response = await this.request({ auth: true, v2: true }).post(
        '/admin/doctorReassignment/out-of-office-requests',
        payload
      );

      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async getPatientReassignments ({
    tab = 'all',
    search,
    filters,
    sortBy = 'startdatestamp',
    sortDir = 'desc',
    limit = 25,
    offset = 0,
  } = {}) {
    try {
      const payload = { tab, limit, offset, sortBy, sortDir };
      if (search) {
        payload.search = search;
      }

      if (filters && filters.length) {
        payload.filters = filters;
      }

      const response = await this.request({ auth: true, v2: true }).post(
        '/admin/adhocReassignment/adhoc-requests',
        payload
      );

      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async getReassignment (id) {
    try {
      const response = await this.request({ auth: true }).get(
        `/doctorreassignment/${id}`
      );

      return new ResponseWrapper(response, response.data.body);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async addReassignment (body) {
    try {
      const response = await this.request({ auth: true, v2: true }).post(
        '/admin/doctorReassignment/request',
        body
      );

      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async updateReassignment (id, body) {
    try {
      const response = await this.request({ auth: true, v2: true }).put(
        `/admin/doctorReassignment/${id}`,
        body
      );

      return new ResponseWrapper(response, response.data.body);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async approveReassignment (id) {
    try {
      const response = await this.request({ auth: true, v2: true }).put(
        `/admin/doctorReassignment/${id}/approve`
      );

      return new ResponseWrapper(response, response.data.body);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async getDoctorEarning (start, end, search = null, includeDisabled = false) {
    try {
      const dateString = start ? `?start=${start}&end=${end}` : '';
      const searchString = search ? `&search=${search}` : '';
      const includeDisabledString = includeDisabled ? '&includeDisabled=1' : '';
      const response = await this.request({ auth: true }).get(
        `/billing${dateString}${searchString}${includeDisabledString}`
      );

      return new ResponseWrapper(response, response.data.body);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async reassignPatient ({
    doctorid,
    patientid,
    assignmenttype,
    enddatestamp
  }) {
    try {
      const response = await this.request({ auth: true, v2: true }).post(
        '/admin/doctorReassignment/transfer/patient',
        {
          doctorid,
          patientid,
          assignmenttype,
          enddatestamp
        }
      );

      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async deleteReassignment (id) {
    try {
      const response = await this.request({ auth: true }).delete(
        `/doctorreassignment/${id}`
      );

      return new ResponseWrapper(response, response.data.body);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async cancelReassignment (id) {
    try {
      const response = await this.request({ auth: true, v2: true }).post(
        `/admin/doctorReassignment/${id}/cancel`,
        {}
      );

      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async getAdhocPanelSummary (doctorId) {
    try {
      const response = await this.request({ auth: true, v2: true }).get(
        `/admin/adhocReassignment/${doctorId}/panel-summary`
      );

      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async submitAdhocReassignment (body) {
    try {
      const response = await this.request({ auth: true, v2: true }).post(
        '/admin/adhocReassignment/reassignment',
        body
      );

      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async convertReassignmentToPermanent (id) {
    try {
      const response = await this.request({ auth: true, v2: true }).put(
        `/admin/doctorReassignment/${id}/make-permanent`
      );

      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async getDoctorAnalytics (start, end, nextpage) {
    try {
      let response;
      if (nextpage) {
        response = await this.request({ auth: true }).get(
          `/admin/doctor/analytic?startdate=${start}&enddate=${end}&nextpage=${nextpage}`
        );
      } else {
        response = await this.request({ auth: true }).get(
          `/admin/doctor/analytic?startdate=${start}&enddate=${end}`
        );
      }

      return new ResponseWrapper(response, response.data.body);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async getDoctorAnalyticsSearch (start, end, searchText) {
    try {
      const response = await this.request({ auth: true }).get(
        `/admin/doctor/analytic/search?startdate=${start}&enddate=${end}&contains=${searchText}`
      );

      return new ResponseWrapper(response, response.data.body);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async patientTransferState (id, body) {
    try {
      const response = await this.request({ auth: true, v2: true }).post(
        `/admin/patientTransferState/${id}`,
        body
      );

      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async getPatientCountByDoctor (doctorId) {
    try {
      const response = await this.request({ auth: true, v2: true }).get(
        `/admin/doctorPatientCounts/${doctorId}`
      )
      
      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async getDelinquentReport (doctorId, fromDate, toDate) {
    try {
      const response = await this.request({ auth: true, v2: true }).get(
        '/admin/reports/delinquent',
        {
          params: {
            doctorId,
            fromDate,
            toDate
          }
        }
      );

      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async getLatestDelinquentReport () {
    try {
      const response = await this.request({ auth: true, v2: true }).get(
        '/admin/reports/delinquent/latest'
      );

      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }

  static async refreshDelinquentReport (reportId) {
    try {
      const response = await this.request({ auth: true, v2: true }).post(
        '/admin/reports/delinquent/generate',
        null,
        {
          params: {
            reportId
          }
        }
      );

      return new ResponseWrapper(response, response.data);
    } catch (error) {
      throw new ErrorWrapper(error);
    }
  }
}
